import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useCurrency } from './CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const { currency } = useCurrency();
  
  const getSessionId = () => {
    let sessionId = localStorage.getItem('chaleur_session');
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      localStorage.setItem('chaleur_session', sessionId);
    }
    return sessionId;
  };

  const fetchCart = useCallback(async () => {
    try {
      const sessionId = getSessionId();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/cart`, {
        params: { session_id: sessionId, currency },
        headers
      });
      setCart(response.data || { items: [], subtotal: 0 });
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCart({ items: [], subtotal: 0 });
    } finally {
      setLoading(false);
    }
  }, [token, currency]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, variantId, quantity = 1, giftWrap = false, giftMessage = null) => {
    setLoading(true);
    try {
      const sessionId = getSessionId();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/cart/add`, {
        product_id: productId,
        variant_id: variantId,
        quantity,
        gift_wrap: giftWrap,
        gift_message: giftMessage
      }, {
        params: { session_id: sessionId },
        headers
      });
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    setLoading(true);
    try {
      const sessionId = getSessionId();
      await axios.put(`${API}/cart/update/${itemId}`, null, {
        params: { session_id: sessionId, quantity }
      });
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    setLoading(true);
    try {
      const sessionId = getSessionId();
      await axios.delete(`${API}/cart/remove/${itemId}`, {
        params: { session_id: sessionId }
      });
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCartCount = () => {
    return cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  };

  const clearCart = () => {
    setCart({ items: [], subtotal: 0 });
    localStorage.removeItem('chaleur_session');
  };

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addToCart,
      updateCartItem,
      removeFromCart,
      fetchCart,
      getCartCount,
      clearCart,
      sessionId: getSessionId()
    }}>
      {children}
    </CartContext.Provider>
  );
};
